import { useState } from 'react'
import { formatMoney } from '../lib/format.js'

// Plan picker modal. Shows the tiers the customer can subscribe to,
// with a monthly/annual toggle. Tapping a plan kicks off Stripe
// Checkout via the parent's onSelect handler.
//
// The Founder Cohort tier is shown only when standard tiers haven't
// been priced yet (i.e. very early days). Once standard prices are
// live, founder is invite-only via direct DM and we hide it from
// the public picker — so customers don't see "$49 forever" alongside
// "$69" and feel cheated. (For now we always show it; flip the
// HIDE_FOUNDER constant when the public-only pricing era starts.)
const HIDE_FOUNDER_FROM_PICKER = false

export default function PlanSelectModal({
  open,
  onClose,
  tiers,
  currentTierId,
  onSelect,
}) {
  const [billing, setBilling] = useState('monthly')
  const [submitting, setSubmitting] = useState(null) // tier_id being submitted

  if (!open) return null

  const visibleTiers = (tiers || [])
    .filter((t) => HIDE_FOUNDER_FROM_PICKER ? t.id !== 'founder' : true)
    // Annual not offered for the Founder Cohort — stays monthly to
    // keep the "$49 forever" simple.
    .map((t) => ({
      ...t,
      annualOffered: t.id !== 'founder' && !!t.stripe_price_id_annual,
    }))

  async function handleSelect(tier) {
    if (submitting) return
    setSubmitting(tier.id)
    try {
      const useBilling = tier.id === 'founder' ? 'monthly' : billing
      await onSelect?.({ tierId: tier.id, billing: useBilling })
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />
      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Choose a plan"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Choose a plan</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Monthly / annual toggle (annual = 20% off) */}
        <div className="mx-4 mb-4">
          <div className="grid grid-cols-2 gap-1 rounded-lg border border-black/10 bg-black/[0.02] p-1">
            <button
              type="button"
              onClick={() => setBilling('monthly')}
              className={`rounded-md px-3 py-2 text-sm font-medium ${
                billing === 'monthly' ? 'bg-white text-blue-700 shadow-sm' : 'text-black/55'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBilling('annual')}
              className={`rounded-md px-3 py-2 text-sm font-medium ${
                billing === 'annual' ? 'bg-white text-blue-700 shadow-sm' : 'text-black/55'
              }`}
            >
              Annual <span className="text-emerald-600">· 20% off</span>
            </button>
          </div>
        </div>

        <div className="space-y-3 px-4 pb-8">
          {visibleTiers.map((tier) => {
            const isCurrent  = tier.id === currentTierId
            const isFounder  = tier.id === 'founder'
            const useBilling = isFounder ? 'monthly' : billing
            const monthlyDollars = tier.monthly_cents / 100
            const annualDollars  = monthlyDollars * 12 * 0.8 // 20% off
            const dollars        = useBilling === 'annual' ? annualDollars / 12 : monthlyDollars
            const cadenceLabel   = useBilling === 'annual' ? '/mo, billed annually' : '/mo'
            const priceUnavailable =
              (useBilling === 'annual'  && !tier.stripe_price_id_annual && !isFounder) ||
              (useBilling === 'monthly' && !tier.stripe_price_id_monthly)

            return (
              <button
                type="button"
                key={tier.id}
                onClick={() => handleSelect(tier)}
                disabled={isCurrent || submitting === tier.id || priceUnavailable}
                className={`block w-full rounded-2xl border-2 p-4 text-left transition ${
                  isCurrent
                    ? 'border-emerald-300 bg-emerald-50'
                    : isFounder
                      ? 'border-amber-brand-200 bg-gradient-to-br from-amber-brand-50 to-amber-50 hover:border-amber-brand-400'
                      : 'border-black/10 bg-white hover:border-blue-300'
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-medium">{tier.display_name}</span>
                      {isCurrent && (
                        <span className="inline-flex items-center rounded-full bg-status-green-bg px-2 py-0.5 text-[11px] font-medium text-status-green-text">
                          Current
                        </span>
                      )}
                      {isFounder && (
                        <span className="inline-flex items-center rounded-full bg-amber-brand-50 px-2 py-0.5 text-[11px] font-medium text-amber-brand-800">
                          Locked forever
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-black/65">
                      {DESCRIPTIONS[tier.id]}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-medium tabular-nums">
                      {formatMoney(dollars)}
                    </div>
                    <div className="text-xs text-black/55">{cadenceLabel}</div>
                  </div>
                </div>
                {priceUnavailable && (
                  <div className="mt-3 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
                    Not yet available — Stripe Price ID hasn't been configured for this option.
                  </div>
                )}
                {tier.trial_days > 0 && !priceUnavailable && !isCurrent && (
                  <div className="mt-2 text-xs text-black/55">
                    {tier.trial_days}-day free trial. Cancel any time.
                  </div>
                )}
                {submitting === tier.id && (
                  <div className="mt-2 text-xs font-medium text-blue-700">
                    Redirecting to secure checkout…
                  </div>
                )}
              </button>
            )
          })}
        </div>

        <p className="px-4 pb-6 text-center text-xs text-black/45">
          Secure payment via Stripe. We never see your card number.
        </p>
      </div>
    </div>
  )
}

const DESCRIPTIONS = {
  founder:
    '1 location · all features · $49/mo locked forever for the first 10 Square users.',
  solo:
    '1 location · all AI features · Square + Toast auto-sync · compliance docs · marketing drafts.',
  growth:
    '2–5 locations · adds: multi-location roll-up · QuickBooks push · churn detection · priority support.',
  pro:
    '6–15 locations · adds: Plaid bank reconciliation · vendor catalog APIs · loss prevention · CSM.',
}
